import {
  Initiative,
  InitiativeDashboardKPIs,
  InitiativeMilestone,
  InitiativeWorkstream,
  InitiativeActionBridge,
  InitiativeProgressUpdate,
  InitiativeRisk,
  InitiativeClosureRecord,
  InitiativeStatusHistory,
  InitiativeHealthHistory,
  InitiativeVersion,
  InitiativeCrossLink
} from './types';

const BASE_URL = 'http://localhost:3000/api/initiatives';

const defaultHeaders = {
  'Content-Type': 'application/json',
  'x-organization-id': 'ORG-XYZ'
};

export const initiativesService = {
  async getDashboardKPIs(): Promise<InitiativeDashboardKPIs> {
    const res = await fetch(`${BASE_URL}/analytics/dashboard`, { headers: defaultHeaders });
    const json = await res.json();
    return json.data;
  },

  async getInitiatives(filters?: {
    status?: string;
    health_status?: string;
    initiative_type?: string;
    priority?: string;
    search?: string;
  }): Promise<Initiative[]> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.health_status) params.append('health_status', filters.health_status);
    if (filters?.initiative_type) params.append('initiative_type', filters.initiative_type);
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.search) params.append('search', filters.search);

    const url = `${BASE_URL}?${params.toString()}`;
    const res = await fetch(url, { headers: defaultHeaders });
    const json = await res.json();
    return json.data;
  },

  async getInitiativeById(id: string): Promise<Initiative> {
    const res = await fetch(`${BASE_URL}/${id}`, { headers: defaultHeaders });
    const json = await res.json();
    return json.data;
  },

  async createInitiative(payload: Partial<Initiative>): Promise<Initiative> {
    const res = await fetch(BASE_URL, {
      method: 'POST',
      headers: defaultHeaders,
      body: JSON.stringify(payload)
    });
    const json = await res.json();
    if (!json.success) throw new Error(json.error || 'Inisiyatif kaydedilemedi');
    return json.data;
  },

  async updateInitiative(id: string, payload: Partial<Initiative>): Promise<Initiative> {
    const res = await fetch(`${BASE_URL}/${id}`, {
      method: 'PUT',
      headers: defaultHeaders,
      body: JSON.stringify(payload)
    });
    const json = await res.json();
    if (!json.success) throw new Error(json.error || 'Inisiyatif guncellenemedi');
    return json.data;
  },

  async transitionStatus(id: string, toStatus: string, reason: string): Promise<Initiative> {
    const res = await fetch(`${BASE_URL}/${id}/transition`, {
      method: 'POST',
      headers: defaultHeaders,
      body: JSON.stringify({ to_status: toStatus, reason })
    });
    const json = await res.json();
    if (!json.success) throw new Error(json.error || 'Statu degistirilemedi');
    return json.data;
  },

  async getStatusHistory(id: string): Promise<InitiativeStatusHistory[]> {
    const res = await fetch(`${BASE_URL}/${id}/status-history`, { headers: defaultHeaders });
    const json = await res.json();
    return json.data;
  },

  async getHealthHistory(id: string): Promise<InitiativeHealthHistory[]> {
    const res = await fetch(`${BASE_URL}/${id}/health-history`, { headers: defaultHeaders });
    const json = await res.json();
    return json.data;
  },

  async getVersions(id: string): Promise<InitiativeVersion[]> {
    const res = await fetch(`${BASE_URL}/${id}/versions`, { headers: defaultHeaders });
    const json = await res.json();
    return json.data;
  },

  async getWorkstreams(id: string): Promise<InitiativeWorkstream[]> {
    const res = await fetch(`${BASE_URL}/${id}/workstreams`, { headers: defaultHeaders });
    const json = await res.json();
    return json.data;
  },

  async createWorkstream(id: string, payload: Partial<InitiativeWorkstream>): Promise<InitiativeWorkstream> {
    const res = await fetch(`${BASE_URL}/${id}/workstreams`, {
      method: 'POST',
      headers: defaultHeaders,
      body: JSON.stringify(payload)
    });
    const json = await res.json();
    return json.data;
  },

  async getMilestones(id: string): Promise<InitiativeMilestone[]> {
    const res = await fetch(`${BASE_URL}/${id}/milestones`, { headers: defaultHeaders });
    const json = await res.json();
    return json.data;
  },

  async createMilestone(id: string, payload: Partial<InitiativeMilestone>): Promise<InitiativeMilestone> {
    const res = await fetch(`${BASE_URL}/${id}/milestones`, {
      method: 'POST',
      headers: defaultHeaders,
      body: JSON.stringify(payload)
    });
    const json = await res.json();
    return json.data;
  },

  async completeMilestone(id: string, milestoneId: string, evidence: string, completedBy: string): Promise<InitiativeMilestone> {
    const res = await fetch(`${BASE_URL}/${id}/milestones/${milestoneId}/complete`, {
      method: 'POST',
      headers: defaultHeaders,
      body: JSON.stringify({ evidence_reference: evidence, completed_by: completedBy })
    });
    const json = await res.json();
    return json.data;
  },

  async getLinkedActions(id: string): Promise<InitiativeActionBridge[]> {
    const res = await fetch(`${BASE_URL}/${id}/actions`, { headers: defaultHeaders });
    const json = await res.json();
    return json.data;
  },

  async linkCentralAction(id: string, payload: any): Promise<InitiativeActionBridge> {
    const res = await fetch(`${BASE_URL}/${id}/actions`, {
      method: 'POST',
      headers: defaultHeaders,
      body: JSON.stringify(payload)
    });
    const json = await res.json();
    return json.data;
  },

  async updateLinkedActionStatus(id: string, bridgeId: string, status: string): Promise<InitiativeActionBridge> {
    const res = await fetch(`${BASE_URL}/${id}/actions/${bridgeId}/status`, {
      method: 'PATCH',
      headers: defaultHeaders,
      body: JSON.stringify({ contribution_status: status })
    });
    const json = await res.json();
    return json.data;
  },

  async getProgressUpdates(id: string): Promise<InitiativeProgressUpdate[]> {
    const res = await fetch(`${BASE_URL}/${id}/progress`, { headers: defaultHeaders });
    const json = await res.json();
    return json.data;
  },

  async recordProgressUpdate(id: string, payload: any): Promise<InitiativeProgressUpdate> {
    const res = await fetch(`${BASE_URL}/${id}/progress`, {
      method: 'POST',
      headers: defaultHeaders,
      body: JSON.stringify(payload)
    });
    const json = await res.json();
    return json.data;
  },

  async recalculate(id: string): Promise<any> {
    const res = await fetch(`${BASE_URL}/${id}/recalculate`, {
      method: 'POST',
      headers: defaultHeaders
    });
    const json = await res.json();
    return json.data;
  },

  async getRisks(id: string): Promise<InitiativeRisk[]> {
    const res = await fetch(`${BASE_URL}/${id}/risks`, { headers: defaultHeaders });
    const json = await res.json();
    return json.data;
  },

  async addRisk(id: string, payload: any): Promise<InitiativeRisk> {
    const res = await fetch(`${BASE_URL}/${id}/risks`, {
      method: 'POST',
      headers: defaultHeaders,
      body: JSON.stringify(payload)
    });
    const json = await res.json();
    return json.data;
  },

  async getCrossLinks(id: string): Promise<InitiativeCrossLink[]> {
    const res = await fetch(`${BASE_URL}/${id}/cross-links`, { headers: defaultHeaders });
    const json = await res.json();
    return json.data;
  },

  async checkClosureReadiness(id: string): Promise<any> {
    const res = await fetch(`${BASE_URL}/${id}/closure-readiness`, { headers: defaultHeaders });
    const json = await res.json();
    return json.data;
  },

  async closeInitiative(id: string, payload: any): Promise<InitiativeClosureRecord> {
    const res = await fetch(`${BASE_URL}/${id}/close`, {
      method: 'POST',
      headers: defaultHeaders,
      body: JSON.stringify(payload)
    });
    const json = await res.json();
    if (!json.success) throw new Error(json.error || 'Inisiyatif kapatilamadi');
    return json.data;
  },

  async getClosureRecord(id: string): Promise<InitiativeClosureRecord | null> {
    const res = await fetch(`${BASE_URL}/${id}/closure`, { headers: defaultHeaders });
    const json = await res.json();
    return json.data;
  }
};
